import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone } from "./icons";

const Logo = ({ href }) => (
  <a className="brand" href={href} aria-label="SAFE MOUV, accueil">
    <span className="brand-mark" aria-hidden="true">
      <svg viewBox="0 0 40 46" width="34" height="39">
        <path
          d="M20 1.5 38 8v14.5C38 33 30.4 41.6 20 44.5 9.6 41.6 2 33 2 22.5V8L20 1.5z"
          fill="none"
          stroke="url(#brandGrad)"
          strokeWidth="2.4"
        />
        <defs>
          <linearGradient id="brandGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f3a8c4" />
            <stop offset="100%" stopColor="#d9628c" />
          </linearGradient>
        </defs>
        <text
          x="20"
          y="29"
          textAnchor="middle"
          fontFamily="Poppins, sans-serif"
          fontSize="20"
          fontWeight="700"
          fill="#ea88ac"
        >
          S
        </text>
      </svg>
    </span>
    <span className="brand-text">
      <strong>SAFE MOUV</strong>
      <em>Votre chauffeure VTC</em>
    </span>
  </a>
);

export const Navigation = ({ data, brand }) => {
  const links = useMemo(() => data || [], [data]);
  const location = useLocation();
  const onHomePage = location.pathname === "/";
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(links[0]?.href || "#accueil");

  // Les ancres (#section) ne fonctionnent que sur l'accueil : ailleurs, on
  // repasse par "/" pour y revenir avant de sauter à la section.
  const toHref = (href) => (href.startsWith("#") && !onHomePage ? `/${href}` : href);

  // Hors de l'accueil, surligne le lien correspondant à la page actuelle.
  useEffect(() => {
    if (!onHomePage) setActive(location.pathname);
  }, [onHomePage, location.pathname]);

  // Surligne le lien de la section actuellement visible (accueil uniquement).
  useEffect(() => {
    if (!onHomePage) return;
    const sections = links
      .map((l) => document.querySelector(l.href))
      .filter(Boolean);
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(`#${visible.target.id}`);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [links, onHomePage]);

  return (
    <nav id="menu" className="nav">
      <div className="container nav-inner">
        <Logo href={toHref("#accueil")} />

        <button
          type="button"
          className={`nav-burger ${open ? "is-open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="Ouvrir le menu"
        >
          <span />
          <span />
          <span />
        </button>

        <ul className={`nav-links ${open ? "is-open" : ""}`}>
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={toHref(link.href)}
                className={active === link.href ? "is-active" : ""}
                onClick={() => setOpen(false)}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <a href="/ramassage-scolaire" className="btn btn-primary nav-cta">
          Ramassage scolaire
        </a>

        <a className="nav-phone" href={`tel:${brand?.phoneLink || ""}`}>
          <span className="nav-phone-icon">
            <Phone size={17} />
          </span>
          <span className="nav-phone-text">
            <strong>{brand?.phone || ""}</strong>
            <em>{brand?.availability || ""}</em>
          </span>
        </a>
      </div>
    </nav>
  );
};

export default Navigation;
